/**
 * Cableado entre las mutaciones del PMIS y `notificarHandover`.
 *
 * Cada mutación de estado (useChangePrState, useChangePoState, …)
 * llama aquí pasando la entidad después del save y la transición
 * que ocurrió. Este módulo:
 *
 *   1. Mapea `(entidad, estadoAnterior → estadoNuevo)` a uno o más
 *      `handoverId`s del catálogo.
 *   2. Construye el `MailContext` desde la entidad (extracción de
 *      código, nombre, totales, URL deep-link).
 *   3. Llama a `notificarHandover` para cada handover mapeado.
 *
 * Es fire-and-forget: si fallas o no hay ningún handover mapeado,
 * la mutación sigue normal.
 *
 * Las tablas de mapeo viven juntas para que un humano pueda revisar
 * de un vistazo qué transición dispara qué correo.
 */

import type {
  Pr,
  Po,
  So,
  Rd,
  Acta,
  Afe,
  Evento,
  ContratoMarco,
  Budget,
  Usuario,
  Riesgo,
  Stakeholder,
} from "@app-types/entities";
import type { MailContext } from "./mailContext";
import { notificarHandover } from "./notificarHandover";

// ── URLs deep-link ───────────────────────────────────────────────────

const RUTA_POR_TIPO: Record<MailContext["entidad"]["tipo"], string> = {
  Budget: "/budget",
  AFE: "/afe",
  PR: "/pr",
  PO: "/po",
  SO: "/so",
  RD: "/rd",
  Acta: "/actas",
  "Contrato Marco": "/contratos",
  Evento: "/eventos",
  Usuario: "/autoridades",
  Riesgo: "/riesgos",
  Stakeholder: "/stakeholders",
  MR: "/mr",
};

function armarUrl(tipo: MailContext["entidad"]["tipo"], id: string): string {
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://pmis";
  const ruta = RUTA_POR_TIPO[tipo];
  // Para SO la página principal es /so/{id}/cotizacion; para los demás /tipo/{id}.
  if (tipo === "SO") return `${base}${ruta}/${id}/cotizacion`;
  return `${base}${ruta}/${id}`;
}

// ── Mapeos de transición → handoverId ────────────────────────────────

/**
 * Mapeo PR. Cuando el estado pasa de A → B, dispara los handoverIds
 * listados. Si la entrada es `*` significa "cualquier estado anterior".
 */
const MAPEO_PR: Array<{ de: string | "*"; a: Pr["estado"]; handoverIds: string[] }> = [
  // PR-1: solicitante crea PR → notifica PL del AFE.
  { de: "*", a: "CREADA_NO_APROBADA", handoverIds: ["PR-1"] },
  // PR-2: PL aprueba PR → notifica ABAS + solicitante.
  { de: "CREADA_NO_APROBADA", a: "APROBADA", handoverIds: ["PR-2"] },
  // PR-3: ABAS inicia ET → notifica FAC asignado + solicitante.
  { de: "APROBADA", a: "ET_FAC", handoverIds: ["PR-3"] },
  // PR-4: FAC termina ET (pasa a EE ABAS) → notifica ABAS + solicitante + PL.
  { de: "ET_FAC", a: "EE_ABAS", handoverIds: ["PR-4"] },
  // PR-5: primera PO emitida → notifica solicitante + PL + proveedor.
  { de: "EE_ABAS", a: "ADJ_PARCIAL", handoverIds: ["PR-5"] },
  // PR-6: PR alcanza adjudicación total → notifica solicitante + PL + JEFE.
  { de: "*", a: "ADJ_TOTAL", handoverIds: ["PR-6"] },
  // PR-9: PR cancelada (handover deshabilitado por default, pero el cable existe).
  { de: "*", a: "CANCELADA", handoverIds: ["PR-9"] },
];

const MAPEO_PO: Array<{ de: string | "*"; a: Po["estado"]; handoverIds: string[] }> = [
  // PO-1: PO emitida → notifica proveedor + comprador + solicitante + PL.
  { de: "*", a: "EMITIDA", handoverIds: ["PO-1"] },
  // PO-3: entrega registrada → notifica proveedor + solicitante + Tesoreria.
  { de: "*", a: "ENTREGADA", handoverIds: ["PO-3"] },
  // PO-6: pago registrado → notifica proveedor + solicitante + PL + Contabilidad.
  { de: "ENTREGADA", a: "PAGADA", handoverIds: ["PO-6"] },
  // PO-8: PO cancelada → notifica proveedor + solicitante + PL.
  { de: "*", a: "CANCELADA", handoverIds: ["PO-8"] },
];

const MAPEO_SO: Array<{ de: string | "*"; a: So["estado"]; handoverIds: string[] }> = [
  // SO-1: contratista envía cotización a revisión → notifica PL2 + back-PL + INT
  // (revisión en paralelo: el INT recibe para dar su concepto consultivo).
  { de: "EN_COTIZACION", a: "EN_REVISION", handoverIds: ["SO-1"] },
  // SO-2: PL2 devuelve SO al contratista → notifica PLANNER + ADOR.
  { de: "EN_REVISION", a: "EN_COTIZACION", handoverIds: ["SO-2"] },
  // SO-3: PL2 aprueba SO → notifica contratista + INT + ABAS + PL del AFE.
  { de: "EN_REVISION", a: "APROBADA", handoverIds: ["SO-3"] },
  // SO-4: reprogramación (APROBADA → EN_REVISION) → notifica contratista + INT + PL del AFE.
  { de: "APROBADA", a: "EN_REVISION", handoverIds: ["SO-4"] },
  // SO-5: SO cancelada → notifica contratista + INT + PL + Contabilidad.
  { de: "*", a: "CANCELADA", handoverIds: ["SO-5"] },
];

const MAPEO_RD: Array<{ de: string | "*"; a: Rd["estado"]; handoverIds: string[] }> = [
  // RD-1: contratista envía RD a revisión por vía normal → notifica INT.
  // Nota: RD-2 (escalado directo a PL2) se dispara cuando nivelRevision="PL" en el cambio,
  //       NO solo por estado. Se maneja en el cableado con la lógica extra abajo.
  { de: "BORRADOR", a: "EN_REVISION", handoverIds: ["RD-1"] },
  // RD-5: PL2 integra el RD → notifica contratista + INT + PL + ADMIN_PMIS.
  { de: "EN_REVISION", a: "APROBADO", handoverIds: ["RD-5"] },
  // RD-7: RD cancelado (handover deshabilitado por default).
  { de: "*", a: "CANCELADO", handoverIds: ["RD-7"] },
  // RD-4: el PL2 devuelve el RD al contratista (rechazo) → notifica PLANNER + ADOR.
  { de: "EN_REVISION", a: "BORRADOR", handoverIds: ["RD-4"] },
  // RD-4 (re-edición): el PL2 habilita la re-edición de un RD aprobado (flujo
  // canónico pt. 13) → el contratista recibe el aviso para hacer los ajustes.
  { de: "APROBADO", a: "BORRADOR", handoverIds: ["RD-4"] },
];

// ── Construcción del MailContext por entidad ─────────────────────────

interface Actor {
  email: string;
  nombre?: string;
  rol?: string;
}

function ctxBase(
  tipo: MailContext["entidad"]["tipo"],
  e: { id: string; codigo?: string; nombre?: string },
  actor: Actor,
  extra: Partial<Pick<MailContext, "motivo" | "totales">> = {},
): MailContext {
  return {
    actor: {
      email: actor.email,
      ...(actor.nombre !== undefined && { nombre: actor.nombre }),
      ...(actor.rol !== undefined && { rol: actor.rol }),
    },
    entidad: {
      tipo,
      codigo: e.codigo ?? e.id.slice(0, 8),
      id: e.id,
      ...(e.nombre !== undefined && { nombre: e.nombre }),
    },
    url: armarUrl(tipo, e.id),
    fecha: new Date().toISOString(),
    ...(extra.motivo !== undefined && { motivo: extra.motivo }),
    ...(extra.totales !== undefined && { totales: extra.totales }),
  };
}

// ── Disparadores públicos ────────────────────────────────────────────

/**
 * Notifica la transición de estado de una PR. Llamarlo desde el
 * `mutationFn` de `useChangePrState` justo después del cambio exitoso.
 */
export async function notificarTransicionPr(args: {
  tenantId: string;
  pr: Pr;
  estadoAnterior: Pr["estado"];
  estadoNuevo: Pr["estado"];
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_PR, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "PR",
    { id: args.pr.id, codigo: args.pr.codigo, nombre: args.pr.concepto },
    args.actor,
    {
      ...(args.motivo !== undefined && { motivo: args.motivo }),
      ...(args.pr.montoUsd !== undefined && { totales: { montoUsd: args.pr.montoUsd } }),
    },
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

/**
 * Notifica la creación de una PR (handover PR-1). Llamarlo desde
 * `useCreatePr.mutationFn` después del create exitoso.
 */
export async function notificarCreacionPr(args: {
  tenantId: string;
  pr: Pr;
  actor: Actor;
}): Promise<void> {
  const ctx = ctxBase(
    "PR",
    { id: args.pr.id, codigo: args.pr.codigo, nombre: args.pr.concepto },
    args.actor,
    args.pr.montoUsd !== undefined ? { totales: { montoUsd: args.pr.montoUsd } } : {},
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId: "PR-1", context: ctx });
}

export async function notificarTransicionPo(args: {
  tenantId: string;
  po: Po;
  estadoAnterior: Po["estado"];
  estadoNuevo: Po["estado"];
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_PO, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "PO",
    { id: args.po.id, codigo: args.po.codigo, nombre: args.po.descripcion },
    args.actor,
    {
      ...(args.motivo !== undefined && { motivo: args.motivo }),
      ...(args.po.montoUsd !== undefined && { totales: { montoUsd: args.po.montoUsd } }),
    },
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

export async function notificarTransicionSo(args: {
  tenantId: string;
  so: So;
  estadoAnterior: So["estado"];
  estadoNuevo: So["estado"];
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_SO, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "SO",
    { id: args.so.id, codigo: args.so.codigo, nombre: args.so.nombre },
    args.actor,
    {
      ...(args.motivo !== undefined && { motivo: args.motivo }),
      ...(args.so.montoUsd !== undefined && { totales: { montoUsd: args.so.montoUsd } }),
    },
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

/**
 * Notifica la transición de un RD. Tiene lógica extra:
 *   - RD-2: si pasa a EN_REVISION con `nivelRevision="PL"` (escalado
 *     directo del contratista al PL2, saltándose al INT), dispara RD-2.
 *   - RD-3: si EN_REVISION cambió de nivelRevision INT → PL (INT aprobó
 *     y escaló), dispara RD-3.
 *   - RD-6: si EN_REVISION → EN_REVISION con cambio de nivel PL → INT
 *     (PL2 devolvió al INT), dispara RD-6.
 */
export async function notificarTransicionRd(args: {
  tenantId: string;
  rd: Rd;
  estadoAnterior: Rd["estado"];
  estadoNuevo: Rd["estado"];
  nivelAnterior?: Rd["nivelRevision"];
  nivelNuevo?: Rd["nivelRevision"];
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const handovers = new Set(
    resolverHandovers(MAPEO_RD, args.estadoAnterior, args.estadoNuevo),
  );
  // Lógica de sub-flag
  if (
    args.estadoAnterior === "BORRADOR" &&
    args.estadoNuevo === "EN_REVISION" &&
    args.nivelNuevo === "PL"
  ) {
    // Escalado directo del contratista a PL2 saltando al INT — RD-2 reemplaza a RD-1.
    handovers.delete("RD-1");
    handovers.add("RD-2");
  }
  if (
    args.estadoAnterior === "EN_REVISION" &&
    args.estadoNuevo === "EN_REVISION" &&
    args.nivelAnterior === "INT" &&
    args.nivelNuevo === "PL"
  ) {
    // INT aprobó y escaló al PL2.
    handovers.add("RD-3");
  }
  if (
    args.estadoAnterior === "EN_REVISION" &&
    args.estadoNuevo === "EN_REVISION" &&
    args.nivelAnterior === "PL" &&
    args.nivelNuevo === "INT"
  ) {
    // PL2 devolvió el RD al INT.
    handovers.add("RD-6");
  }
  if (handovers.size === 0) return;

  const fechaCodigo = args.rd.fecha;
  const ctx = ctxBase(
    "RD",
    { id: args.rd.id, codigo: `RD-${fechaCodigo}` },
    args.actor,
    args.motivo !== undefined ? { motivo: args.motivo } : {},
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

/**
 * RD-11 / RD-12: calificaciones bajas o caída del promedio mensual.
 * Se disparan cuando el PL2 integra el RD (APROBADO) con calificación
 * <= 2 estrellas, o cuando se detecta que el promedio del mes cayó
 * 0.5 puntos vs el mes anterior.
 *
 * RD-11 lo dispara `useUpsertRdDia` cuando aprueba con calificación baja.
 * RD-12 requiere comparar histórico — se maneja en otro punto del flujo
 * (cuando el seed agrega o se cierra el mes), TODO en 3.4.c.
 */
export async function notificarCalificacionBajaRd(args: {
  tenantId: string;
  rd: Rd;
  actor: Actor;
}): Promise<void> {
  if (!args.rd.calificacionInt || args.rd.calificacionInt > 2) return;
  const ctx = ctxBase(
    "RD",
    { id: args.rd.id, codigo: `RD-${args.rd.fecha}` },
    args.actor,
    { motivo: `Calificación INT: ${args.rd.calificacionInt}/5` },
  );
  await notificarHandover({
    tenantId: args.tenantId,
    handoverId: "RD-11",
    context: ctx,
  });
}

// ── Helpers internos ─────────────────────────────────────────────────

function resolverHandovers<S extends string>(
  mapeo: Array<{ de: string | "*"; a: S; handoverIds: string[] }>,
  estadoAnterior: S,
  estadoNuevo: S,
): string[] {
  return mapeo
    .filter((m) => (m.de === "*" || m.de === estadoAnterior) && m.a === estadoNuevo)
    .flatMap((m) => m.handoverIds);
}

// ── Mapeos y disparadores: Acta ──────────────────────────────────────

const MAPEO_ACTA: Array<{
  de: string | "*";
  a: Acta["estado"];
  handoverIds: string[];
}> = [
  // AC-3: contratista envía acta al INT (BORRADOR → EN_REVISION_INT).
  { de: "BORRADOR", a: "EN_REVISION_INT", handoverIds: ["AC-3"] },
  // AC-4: INT aprueba acta (EN_REVISION_INT → APROBADA_INT).
  { de: "EN_REVISION_INT", a: "APROBADA_INT", handoverIds: ["AC-4"] },
  // AC-5: INT rechaza acta (EN_REVISION_INT → BORRADOR/RECHAZADA).
  { de: "EN_REVISION_INT", a: "BORRADOR", handoverIds: ["AC-5"] },
  { de: "EN_REVISION_INT", a: "RECHAZADA", handoverIds: ["AC-5"] },
  // AC-6: PL2 aprueba acta (cualquier estado de revisión → APROBADA).
  { de: "APROBADA_INT", a: "APROBADA", handoverIds: ["AC-6"] },
  { de: "EN_REVISION_PL2", a: "APROBADA", handoverIds: ["AC-6"] },
  // AC-7: acta pasa a FACTURADA (APROBADA → FACTURADA).
  { de: "APROBADA", a: "FACTURADA", handoverIds: ["AC-7"] },
  // AC-8: PL2 rechaza acta (EN_REVISION_PL2 → RECHAZADA o BORRADOR).
  { de: "EN_REVISION_PL2", a: "RECHAZADA", handoverIds: ["AC-8"] },
  { de: "EN_REVISION_PL2", a: "BORRADOR", handoverIds: ["AC-8"] },
  // AC-11: el acta se ANULA. Desde CUALQUIER estado, porque se puede anular en
  // cualquier punto del ciclo. Sin esta entrada, `resolverHandovers` devolvía
  // vacío y anular un acta ya firmada no avisaba a NADIE: contratista,
  // interventor y PL2 se enteraban al abrirla.
  { de: "*", a: "ANULADA", handoverIds: ["AC-11"] },
  // AC-12: el acta vuelve a corrección desde un estado ya decidido. Los pasos
  // BORRADOR desde revisión ya los cubren AC-5 y AC-8; estos dos son la vía
  // nueva, la que usa el PL2 cuando el acta se rechaza FUERA del sistema.
  { de: "APROBADA", a: "BORRADOR", handoverIds: ["AC-12"] },
  { de: "FACTURADA", a: "BORRADOR", handoverIds: ["AC-12"] },
];

export async function notificarTransicionActa(args: {
  tenantId: string;
  acta: Acta;
  estadoAnterior: Acta["estado"];
  estadoNuevo: Acta["estado"];
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_ACTA, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "Acta",
    { id: args.acta.id, codigo: args.acta.codigo },
    args.actor,
    {
      ...(args.motivo !== undefined && { motivo: args.motivo }),
      ...(args.acta.totalUsd !== undefined && { totales: { montoUsd: args.acta.totalUsd } }),
    },
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

// ── Mapeos y disparadores: AFE ──────────────────────────────────────

const MAPEO_AFE: Array<{
  de: string | "*";
  a: Afe["estado"];
  handoverIds: string[];
}> = [
  // A-1: PL crea AFE listo para revisión (BORRADOR → CREADO_NO_APROBADO).
  { de: "BORRADOR", a: "CREADO_NO_APROBADO", handoverIds: ["A-1"] },
  // A-2: JEFE envía a su propia revisión (CREADO_NO_APROBADO → EN_REVISION_JF).
  { de: "CREADO_NO_APROBADO", a: "EN_REVISION_JF", handoverIds: ["A-2"] },
  // A-3: JEFE aprueba AFE (EN_REVISION_JF → APROBADO).
  { de: "EN_REVISION_JF", a: "APROBADO", handoverIds: ["A-3"] },
  // A-4: AFE entra en ejecución (APROBADO → EN_EJECUCION, normalmente automático
  // al crear la primera PR/PO/SO contra él).
  { de: "APROBADO", a: "EN_EJECUCION", handoverIds: ["A-4"] },
  // A-7: PL solicita extensión (EN_EJECUCION → EN_REVISION_JF) o queda EXTENDIDO.
  { de: "EN_EJECUCION", a: "EN_REVISION_JF", handoverIds: ["A-7"] },
  { de: "EN_REVISION_JF", a: "EXTENDIDO", handoverIds: ["A-7"] },
  // A-9: AFE cerrado (cualquier estado → CERRADO).
  { de: "*", a: "CERRADO", handoverIds: ["A-9"] },
];

export async function notificarTransicionAfe(args: {
  tenantId: string;
  afe: Afe;
  estadoAnterior: Afe["estado"];
  estadoNuevo: Afe["estado"];
  actor: Actor;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_AFE, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "AFE",
    { id: args.afe.id, codigo: args.afe.codigo, nombre: args.afe.nombre },
    args.actor,
    args.afe.valorAprobadoUsd !== undefined
      ? { totales: { montoUsd: args.afe.valorAprobadoUsd } }
      : {},
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

// ── Mapeos y disparadores: Contrato Marco ───────────────────────────

const MAPEO_CONTRATO: Array<{
  de: string | "*";
  a: ContratoMarco["estado"];
  handoverIds: string[];
}> = [
  // CM-1: contrato marco firmado/activado (BORRADOR/EN_APROBACION → VIGENTE).
  { de: "BORRADOR", a: "VIGENTE", handoverIds: ["CM-1"] },
  { de: "EN_APROBACION", a: "VIGENTE", handoverIds: ["CM-1"] },
  // CM-5: contrato marco cerrado (cualquier estado → TERMINADO).
  { de: "*", a: "TERMINADO", handoverIds: ["CM-5"] },
];

export async function notificarTransicionContrato(args: {
  tenantId: string;
  contrato: ContratoMarco;
  estadoAnterior: ContratoMarco["estado"];
  estadoNuevo: ContratoMarco["estado"];
  actor: Actor;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_CONTRATO, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "Contrato Marco",
    {
      id: args.contrato.id,
      codigo: args.contrato.codigo,
      ...(args.contrato.nombre !== undefined && { nombre: args.contrato.nombre }),
    },
    args.actor,
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

/**
 * CM-2: incremento IPC aplicado a un contrato marco. Se dispara desde
 * el hook que registra el IPC (típicamente `useUpdateContrato` con un
 * cambio en `incrementosIpc`). Notifica al contratista + ABAS + INT.
 */
export async function notificarIpcContrato(args: {
  tenantId: string;
  contrato: ContratoMarco;
  porcentaje: number;
  actor: Actor;
}): Promise<void> {
  const ctx = ctxBase(
    "Contrato Marco",
    {
      id: args.contrato.id,
      codigo: args.contrato.codigo,
      ...(args.contrato.nombre !== undefined && { nombre: args.contrato.nombre }),
    },
    args.actor,
    { motivo: `Incremento IPC aplicado: ${args.porcentaje.toFixed(2)}%` },
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId: "CM-2", context: ctx });
}

// ── Mapeos y disparadores: Evento ───────────────────────────────────

/**
 * Mapeo del tipo de evento (HSE, AMBIENTAL, CALIDAD, …) al handoverId
 * que se dispara cuando se registra ese evento.
 */
const HANDOVER_POR_TIPO_EVENTO: Record<string, string> = {
  HSE: "EV-1",
  AMBIENTAL: "EV-2",
  CALIDAD: "EV-3",
  SEG_FISICA: "EV-4",
  LEAN: "EV-5",
  LECCION_APRENDIDA: "EV-6",
};

export async function notificarEventoCreado(args: {
  tenantId: string;
  evento: Evento;
  actor: Actor;
}): Promise<void> {
  const handoverId = HANDOVER_POR_TIPO_EVENTO[args.evento.tipo as string];
  if (!handoverId) return;
  const ctx = ctxBase(
    "Evento",
    {
      id: args.evento.id,
      codigo: args.evento.codigo ?? args.evento.id.slice(0, 8),
      nombre: args.evento.descripcion,
    },
    args.actor,
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId, context: ctx });
}

// ── Mapeos y disparadores: Usuario ──────────────────────────────────

export async function notificarUsuarioCreado(args: {
  tenantId: string;
  usuario: Usuario;
  actor: Actor;
}): Promise<void> {
  const ctx = ctxBase(
    "Usuario",
    {
      id: args.usuario.id,
      codigo: args.usuario.email,
      nombre: args.usuario.nombre,
    },
    args.actor,
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId: "AU-1", context: ctx });
}

/**
 * AU-2 / AU-3 / AU-4: cambios en autoridades. La detección de qué
 * handover disparar se basa en qué campos cambiaron:
 *   - AU-3: cambio de rol a PL → notificar PL designado + back-PL.
 *   - AU-4: asignación a una SO como INT → notificar INT + contratista.
 *   - AU-2: cambio en matriz de permisos (rol del usuario).
 *
 * Las dos primeras suelen ocurrir en flujos del AFE/SO (asignación de
 * responsable). AU-2 se dispara cuando ADMIN_PMIS cambia el rol del
 * usuario en `/autoridades`.
 */
export async function notificarCambioRol(args: {
  tenantId: string;
  usuario: Usuario;
  rolAnterior: string;
  actor: Actor;
}): Promise<void> {
  if (args.rolAnterior === args.usuario.rol) return;
  const ctx = ctxBase(
    "Usuario",
    {
      id: args.usuario.id,
      codigo: args.usuario.email,
      nombre: args.usuario.nombre,
    },
    args.actor,
    { motivo: `Rol cambió de ${args.rolAnterior} a ${args.usuario.rol}` },
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId: "AU-2", context: ctx });
}

// ── Mapeos y disparadores: Budget ───────────────────────────────────

const MAPEO_BUDGET: Array<{
  de: string | "*";
  a: Budget["estado"];
  handoverIds: string[];
}> = [
  // B-2: Budget cerrado (cualquier estado → CERRADO).
  { de: "*", a: "CERRADO", handoverIds: ["B-2"] },
];

export async function notificarTransicionBudget(args: {
  tenantId: string;
  budget: Budget;
  estadoAnterior: Budget["estado"];
  estadoNuevo: Budget["estado"];
  actor: Actor;
}): Promise<void> {
  const handovers = resolverHandovers(MAPEO_BUDGET, args.estadoAnterior, args.estadoNuevo);
  if (handovers.length === 0) return;
  const ctx = ctxBase(
    "Budget",
    { id: args.budget.id, codigo: args.budget.codigo },
    args.actor,
  );
  for (const h of handovers) {
    await notificarHandover({ tenantId: args.tenantId, handoverId: h, context: ctx });
  }
}

/**
 * B-3: Budget ejecutado supera el 85% del aprobado. Dispara al
 * cruzar el umbral (no cada vez que se guarda). El llamador debe
 * comparar el porcentaje ANTES y DESPUÉS de la mutación.
 */
export async function notificarUmbralBudget(args: {
  tenantId: string;
  budget: Budget;
  porcentajeAnterior: number;
  porcentajeNuevo: number;
  actor: Actor;
}): Promise<void> {
  const UMBRAL = 0.85;
  // Solo dispara al CRUZAR el umbral hacia arriba.
  if (args.porcentajeAnterior >= UMBRAL || args.porcentajeNuevo < UMBRAL) return;
  const ctx = ctxBase(
    "Budget",
    { id: args.budget.id, codigo: args.budget.codigo },
    args.actor,
    {
      motivo: `Ejecución alcanzó ${(args.porcentajeNuevo * 100).toFixed(1)}% del presupuesto`,
    },
  );
  await notificarHandover({ tenantId: args.tenantId, handoverId: "B-3", context: ctx });
}

// ── Handovers por umbral pendientes (3.4.c) ─────────────────────────
//
// Los siguientes requieren comparación pre/post mutación de valores
// agregados que hoy se calculan en las páginas (no en hooks). Se
// dejan documentados para una siguiente fase:
//
//   - A-5: consumo de un coste cruza 80/100/110% del techo del AFE.
//   - A-6: diferencia en cambio acumulada > 5% del techo del AFE.
//   - PO-7: diferencia en cambio de una PO > 3% del monto.
//   - CM-4: calificación promedio del contratista cae bajo 3.0★.
//   - RD-12: promedio mensual del contratista cae 0.5★ vs mes anterior.
//   - EV-7: 3+ eventos HSE en una misma SO en 7 días.
//   - SO-8: PL2 marca SO como "en cierre" (sub-flag, no transición de estado).
//
// Cuando se conecten: añadir helper aquí y llamarlo desde la mutación
// correspondiente, comparando snapshots before/after.

// ═══════════════════════════════════════════════════════════════════════
// Módulo Riesgos + Stakeholders (F8)
// Ver docs/ALCANCE_RIESGOS_STAKEHOLDERS.md sección 9.
// ═══════════════════════════════════════════════════════════════════════

/**
 * RG-1: Notificar a Sponsor + GTE + PL de AFEs pineados cuando se
 * crea un riesgo CRÍTICO (Score ≥ 15). Llamar desde `useCreateRiesgo`
 * después del create exitoso, solo si el nivel es CRITICO.
 */
export async function notificarRiesgoCriticoCreado(args: {
  tenantId: string;
  riesgo: Riesgo;
  actor: Actor;
}): Promise<void> {
  // Solo riesgos críticos disparan este handover.
  if (args.riesgo.nivel !== "CRITICO") return;
  const ctx = ctxBase(
    "Riesgo",
    {
      id: args.riesgo.id,
      codigo: args.riesgo.codigo,
      nombre: args.riesgo.descripcion.slice(0, 80),
    },
    args.actor,
    { motivo: `Score ${args.riesgo.score} (P${args.riesgo.probabilidad}·I${args.riesgo.impacto})` },
  );
  await notificarHandover({
    tenantId: args.tenantId,
    handoverId: "RG-1",
    context: ctx,
  });
}

/**
 * RG-2: Notificar cuando un riesgo pasa a MATERIALIZADO (el riesgo se
 * "cumplió"). Llamar desde `useChangeRiesgoState` cuando nextState ===
 * "MATERIALIZADO".
 */
export async function notificarRiesgoMaterializado(args: {
  tenantId: string;
  riesgo: Riesgo;
  actor: Actor;
  motivo?: string;
}): Promise<void> {
  const ctx = ctxBase(
    "Riesgo",
    {
      id: args.riesgo.id,
      codigo: args.riesgo.codigo,
      nombre: args.riesgo.descripcion.slice(0, 80),
    },
    args.actor,
    args.motivo !== undefined ? { motivo: args.motivo } : {},
  );
  await notificarHandover({
    tenantId: args.tenantId,
    handoverId: "RG-2",
    context: ctx,
  });
}

/**
 * RG-3: Plan de contingencia activado. Se considera "activado" cuando
 * un riesgo pasa al estado EN_MITIGACION y tiene `planContingencia`
 * no vacío. Llamar desde `useChangeRiesgoState`.
 */
export async function notificarContingenciaActivada(args: {
  tenantId: string;
  riesgo: Riesgo;
  actor: Actor;
}): Promise<void> {
  if (args.riesgo.estado !== "EN_MITIGACION") return;
  if (!args.riesgo.planContingencia.trim()) return;
  const ctx = ctxBase(
    "Riesgo",
    {
      id: args.riesgo.id,
      codigo: args.riesgo.codigo,
      nombre: args.riesgo.descripcion.slice(0, 80),
    },
    args.actor,
    { motivo: `Plan de contingencia: ${args.riesgo.planContingencia}` },
  );
  await notificarHandover({
    tenantId: args.tenantId,
    handoverId: "RG-3",
    context: ctx,
  });
}

/**
 * SK-1: Notificar cuando se registra un stakeholder con engagement
 * BLOQUEA o RESISTENTE. Llamar desde `useCreateStakeholder` y
 * `useUpdateStakeholder` (cuando el engagement cambia a un nivel
 * conflictivo).
 */
export async function notificarStakeholderConflictivo(args: {
  tenantId: string;
  stakeholder: Stakeholder;
  actor: Actor;
}): Promise<void> {
  const eng = args.stakeholder.engagementActual;
  if (eng !== "BLOQUEA" && eng !== "RESISTENTE") return;
  const ctx = ctxBase(
    "Stakeholder",
    {
      id: args.stakeholder.id,
      codigo: args.stakeholder.codigo,
      nombre: `${args.stakeholder.nombre} (${args.stakeholder.rol})`,
    },
    args.actor,
    {
      motivo: `Engagement: ${eng} · Cuadrante: ${args.stakeholder.cuadrante}`,
    },
  );
  await notificarHandover({
    tenantId: args.tenantId,
    handoverId: "SK-1",
    context: ctx,
  });
}
